import type { FC, ReactNode } from 'react'

import {
    ArrowRight,
    Check,
    FileSearch,
    FilePen,
    ScanText,
    Signature,
    ShieldCheck
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'

const APP_URL = 'https://app.rarefrequencyworks.com/'
const PLAY_URL = 'https://play.google.com/store/apps/details?id=works.rarefrequency.ctrl&hl=en_US'
const MS_URL = 'https://apps.microsoft.com/detail/9ntx9m4mtj1l?hl=en-US&gl=US'

const TOOLS = [
    { icon: FilePen, title: 'Edit existing text', text: "Change words and paragraphs already in your PDF, without retyping the page." },
    { icon: ScanText, title: 'OCR text scanner', text: 'Turn scanned pages and photos of documents into text you can copy.' },
    { icon: FileSearch, title: 'Metadata and privacy', text: 'See the hidden author, software and dates stored in a PDF, clean them, and check the saved copy.' },
    { icon: Signature, title: 'Sign documents', text: 'Draw or type a signature, add the date, and check certificate signatures for changes.' }
]

const FREE_FEATURES = [
    '3 saves per day',
    '1 metadata cleanup per day',
    'OCR on the first page',
    'Small "DOCSCRUB FREE" mark on signature exports'
]

const PREMIUM_FEATURES = [
    'Unlimited saves and exports',
    'Unlimited metadata cleanups',
    'OCR on every page',
    'Signatures without the FREE mark',
    'Ad-free where applicable'
]

const PLATFORMS = [
    { name: 'Web', note: 'Free in your browser. Nothing to install. Premium is not available on web.', href: APP_URL, cta: 'Open web app' },
    { name: 'Android', note: 'For your phone or tablet.', href: PLAY_URL, cta: 'Get it on Google Play' },
    { name: 'Windows', note: 'PDF work on your desktop.', href: MS_URL, cta: 'Get it from Microsoft' },
    { name: 'Amazon Fire', note: 'For your Fire tablet.', href: 'https://www.amazon.com/gp/product/B0HL4XBLKN', cta: 'Get it on Amazon' }
]

const DocscrubPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='DOCSCRUB'
                description='Edit existing PDF text, scan to text, sign and remove hidden metadata. Files stay on your device. Free to start, Premium $5.99 a month or $39.99 a year.'
            />

            <div className='min-h-screen bg-[#060b14] text-slate-200'>
                <SiteHeader />

                <section id='hero' className='relative overflow-hidden'>
                    <div className='pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[900px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl' />
                    <div className='relative mx-auto max-w-6xl px-6 pt-20 pb-16 md:pt-28'>
                        <p className='font-mono text-xs tracking-[0.2em] text-cyan-300 uppercase'>DOCSCRUB by Rare Frequency Works</p>
                        <h1 className='mt-5 max-w-4xl text-4xl leading-[1.05] font-extrabold tracking-tight text-white md:text-6xl'>
                            Fix your PDF <span className='font-serif-accent font-normal text-amber-300'>without sending it anywhere.</span>
                        </h1>
                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-slate-400'>
                            Edit text, scan pages, sign and clean hidden details. Your files are processed on your device, never uploaded.
                        </p>
                        <div className='mt-8 flex flex-wrap items-center gap-4'>
                            <a href={APP_URL} className='inline-flex items-center gap-2 rounded-xl bg-cyan-300 px-6 py-3.5 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200'>
                                Open DOCSCRUB free <ArrowRight className='size-4' />
                            </a>
                            <a href='#pricing' className='text-sm font-medium text-cyan-300 underline-offset-4 hover:underline'>See pricing</a>
                        </div>
                        <p className='mt-5 flex items-center gap-2 text-sm text-slate-500'>
                            <ShieldCheck className='size-4 text-cyan-300' /> No account needed to start
                        </p>
                    </div>
                </section>

                <section id='tools' className='mx-auto max-w-6xl px-6 pb-20'>
                    <h2 className='text-2xl font-extrabold text-white md:text-3xl'>Four document tools in one place</h2>
                    <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {TOOLS.map(({ icon: Icon, title, text }) => (
                            <article key={title} className='rounded-2xl border border-white/10 bg-white/[0.02] p-6'>
                                <span className='inline-flex rounded-xl bg-cyan-400/10 p-2.5 text-cyan-300'><Icon className='size-5' /></span>
                                <h3 className='mt-4 font-bold text-white'>{title}</h3>
                                <p className='mt-2 text-sm leading-relaxed text-slate-400'>{text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section id='pricing' className='border-y border-white/10 bg-[#081120]'>
                    <div className='mx-auto max-w-6xl px-6 py-20'>
                        <div className='max-w-2xl'>
                            <h2 className='text-2xl font-extrabold text-white md:text-3xl'>Start free. Upgrade when you need more.</h2>
                            <p className='mt-3 text-slate-400'>One Premium plan with every feature. Pay monthly or save with yearly.</p>
                        </div>

                        <div className='mt-10 grid gap-6 md:grid-cols-2'>
                            <article className='flex flex-col rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8'>
                                <h3 className='text-lg font-bold text-white'>Free</h3>
                                <p className='mt-4 flex items-baseline gap-1'>
                                    <span className='text-4xl font-extrabold text-white'>$0</span>
                                </p>
                                <p className='mt-2 text-sm text-slate-400'>For a quick fix now and then.</p>
                                <ul className='mt-6 mb-8 space-y-3 text-sm text-slate-300'>
                                    {FREE_FEATURES.map((f) => (
                                        <li key={f} className='flex gap-3'><Check className='mt-0.5 size-4 shrink-0 text-slate-500' />{f}</li>
                                    ))}
                                </ul>
                                <a href={APP_URL} className='mt-auto inline-flex justify-center rounded-xl border border-white/15 px-5 py-3 text-sm font-bold text-white transition hover:border-cyan-300/50'>
                                    Start free
                                </a>
                            </article>

                            <article className='relative flex flex-col rounded-3xl border border-cyan-300/40 bg-gradient-to-b from-[#0f1d33] to-[#0a1424] p-6 sm:p-8'>
                                <span className='absolute -top-3 right-6 rounded-full bg-amber-300 sm:right-8 px-3 py-1 text-xs font-bold text-[#06121f]'>Save 44% yearly</span>
                                <h3 className='text-lg font-bold text-white'>Premium</h3>
                                <p className='mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                                    <span className='text-4xl font-extrabold text-white'>$5.99</span>
                                    <span className='text-slate-400'>/ month</span>
                                    <span className='text-sm text-slate-500'>or $39.99 / year</span>
                                </p>
                                <p className='mt-2 text-sm text-slate-400'>For regular PDF work, without limits.</p>
                                <ul className='mt-6 space-y-3 text-sm text-slate-200'>
                                    {PREMIUM_FEATURES.map((f) => (
                                        <li key={f} className='flex gap-3'><Check className='mt-0.5 size-4 shrink-0 text-cyan-300' />{f}</li>
                                    ))}
                                </ul>
                                <div className='mt-auto grid gap-3 pt-8 lg:grid-cols-2'>
                                    <a href={PLAY_URL} className='inline-flex justify-center rounded-xl bg-cyan-300 px-4 py-3 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200'>
                                        Get Premium on Android
                                    </a>
                                    <a href={MS_URL} className='inline-flex justify-center rounded-xl bg-cyan-300 px-4 py-3 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200'>
                                        Get Premium on Windows
                                    </a>
                                </div>
                                <p className='mt-3 text-xs text-slate-500'>Install DOCSCRUB from the store, then upgrade to Premium inside the app.</p>
                            </article>
                        </div>

                        <p className='mt-6 max-w-3xl text-sm text-slate-500'>
                            Prices in USD. Taxes and local pricing are shown at checkout. Premium is purchased inside the Android and Windows apps and does not transfer between the two. The web version is free. Metadata cleanup does not remove visible page content, so review the document itself before sharing.
                        </p>
                    </div>
                </section>

                <section id='download' className='mx-auto max-w-6xl px-6 py-20'>
                    <h2 className='text-2xl font-extrabold text-white md:text-3xl'>Use it wherever you work</h2>
                    <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {PLATFORMS.map((p) => (
                            <a key={p.name} href={p.href} className='group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition hover:border-cyan-300/40'>
                                <h3 className='font-bold text-white'>{p.name}</h3>
                                <p className='mt-2 text-sm text-slate-400'>{p.note}</p>
                                <span className='mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-cyan-300'>
                                    {p.cta} <ArrowRight className='size-4 transition group-hover:translate-x-0.5' />
                                </span>
                            </a>
                        ))}
                    </div>
                </section>

                <SiteFooter />
            </div>
        </Layout>
    )
}

export default DocscrubPage
